/**
 * SocketContext - Single shared Socket.IO connection for the entire application
 * 
 * Industry Standard: One WebSocket connection per browser tab, shared via React Context.
 * Pattern: Same approach used by Slack, Discord, Figma — single persistent connection.
 * 
 * Why: Multiple socket connections (one per component/context) cause:
 *   - 3x the TCP connections per tab
 *   - HTTP polling fallback multiplied per connection
 *   - Wasted server resources (memory per socket)
 *   - Rate limiter hits from polling requests
 * 
 * @module SocketContext
 */
import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { WS_URL } from '../config/api';
import { logger, getErrorMessage } from '../utils/logger';

interface SocketContextType {
  /** The shared Socket.IO instance. May be null during initial connection. */
  socket: Socket | null;
  /** Whether the socket is currently connected */
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

/**
 * SocketProvider - Creates and manages the single Socket.IO connection.
 * 
 * Must be placed in the provider hierarchy ABOVE both TestFlowProvider 
 * and any component that needs real-time updates (e.g., GlobalPipelineModal).
 * 
 * Connection includes auth token if available (for pipeline room access).
 * Reconnects automatically with exponential backoff.
 */
export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Create single socket connection on mount
    const token = localStorage.getItem('token');
    
    const newSocket = io(WS_URL, {
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],  // Prefer WebSocket, fallback to polling
      reconnection: true,
      reconnectionAttempts: Infinity,         // Always try to reconnect
      reconnectionDelay: 1000,               // Start with 1s delay
      reconnectionDelayMax: 30000,           // Max 30s between reconnect attempts
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      logger.debug('[SOCKET] Connected — ID:', { data: newSocket.id });
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      logger.debug('[SOCKET] Disconnected:', { data: reason });
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      logger.error('[SOCKET] Connection error:', { error: getErrorMessage(error) });
      setIsConnected(false);
    });

    return () => {
      logger.debug('[SOCKET] Cleaning up connection');
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Update auth token when it changes (e.g., after login)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      if (socketRef.current) {
        // Update auth for next reconnection attempt
        (socketRef.current as any).auth = token ? { token } : undefined;
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * useSocket - Access the shared Socket.IO connection from any component.
 * 
 * @returns The shared Socket instance and connection status
 * @throws If used outside of SocketProvider
 * 
 * @example
 * ```tsx
 * const { socket } = useSocket();
 * useEffect(() => {
 *   if (!socket) return;
 *   socket.on('my-event', handler);
 *   return () => { socket.off('my-event', handler); };
 * }, [socket]);
 * ```
 */
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

